// src/Pages/Admin/agenda/AdminAgendaPage.jsx
import React, { useEffect, useState, useCallback } from 'react';
import {
  collection,
  onSnapshot,
  query,
  where,
  orderBy,
  getDocs,
  updateDoc,
  doc,
  serverTimestamp,
  Timestamp,
  limit,
  setDoc,
  addDoc,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../../../firebase/config';

import AgendaCalendar from './AgendaCalendar';
import DayAgenda from './DayAgenda';
import SlotModal from './SlotModal';
import { aprobarReservaSegura } from './agendaTx';
import BreakEditorModal from './BreakEditorModal';
import { CalendarClock, Clock4, DollarSign, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';


// ---------- utils ----------
const fmtYMD = (d) => d.toISOString().slice(0, 10);
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const weekdayISO = (dateStr) => {
  const d = new Date(dateStr + 'T00:00:00');
  const n = d.getDay(); // 0(dom) .. 6(sab)
  return n === 0 ? 7 : n; // 1..7
};

// ---------- UI helpers ----------
const Skeleton = ({ className }) => (
  <div className={`animate-pulse bg-gray-200/70 rounded-md ${className}`} />
);



const KpiCard = ({ icon, title, value, accent, onClick }) => (
  <div
    onClick={onClick}
    className={`bg-white border border-gray-100 rounded-2xl shadow p-4 transition
      overflow-hidden
      ${onClick ? "hover:shadow-md cursor-pointer" : ""}
    `}
  >
    <div className="flex items-start justify-between gap-3">
      {/* 👇 clave: min-w-0 para que truncate funcione */}
      <div className="min-w-0">
        <div className="text-xs sm:text-sm text-gray-500 leading-snug">
          {title}
        </div>

        <div className="mt-1 font-bold text-blue-900 tabular-nums leading-tight
                        text-xl sm:text-2xl">
          {value === null ? (
            <Skeleton className="h-7 w-24" />
          ) : (
            // 👇 evita que un string largo empuje el layout
            <span className="block truncate">
              {value}
            </span>
          )}
        </div>
      </div>

      <div
        className={`shrink-0 h-10 w-10 rounded-full flex items-center justify-center ${accent}`}
      >
        {icon}
      </div>
    </div>
  </div>
);



/* =========================
   EKINE: servicios embebidos (aplanados)
   ========================= */
const ekineServicios = (() => {
  const serviciosLanding = [
    {
      title: 'Sesiones de Kinesiología',
      options: ['1 sesión: $28.000', '5 sesiones: $126.000', '10 sesiones: $238.000'],
    },
    {
      title: 'Planes de Entrenamiento',
      options: {
        Mensual: { '2x': '$148.000', '3x': '$191.400', '5x': '$268.000' },
        Trimestral: { '2x': '$377.400', '3x': '$430.650', '5x': '$612.000' },
        Semestral: { '2x': '$710.400', '3x': '$918.720', '5x': '$1.080.000' },
      },
    },
    {
      title: 'Recovery Deportivo',
      options: ['1 sesión: $30.000', '5 sesiones: $50.000'],
    },
    {
      title: 'Masajes Mitad de Semana',
      options: null,
      singleDesc: '$25.000 (antes $35.000)',
    },
    { title: 'Entrenamiento en Pareja', options: 'proximamente' },
    { title: 'Alianzas Estratégicas', options: 'proximamente' },
  ];

  const slug = (s) =>
    String(s || '')
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');

  const out = [];
  serviciosLanding.forEach((srv) => {
    if (srv.options === 'proximamente') return;
    const categoria = srv.title;

    if (Array.isArray(srv.options)) {
      srv.options.forEach((op, i) => {
        const display = `${categoria} — ${op}`;
        out.push({
          id: `${slug(categoria)}-${i}`,
          categoria,
          variante: op,
          display,
          raw: `${categoria} - ${op}`,
        });
      });
      return;
    }
    if (srv.options && typeof srv.options === 'object') {
      Object.entries(srv.options).forEach(([periodo, freqs]) => {
        Object.entries(freqs).forEach(([freq, precio], j) => {
          const variante = `${periodo} ${freq}: ${precio}`;
          const display = `${categoria} — ${variante}`;
          out.push({
            id: `${slug(categoria)}-${slug(periodo)}-${slug(freq)}-${j}`,
            categoria,
            variante,
            display,
            raw: `${categoria} - ${variante}`,
          });
        });
      });
      return;
    }
    if (!srv.options) {
      const variante = srv.singleDesc || '$25.000';
      const display = `${categoria} — ${variante}`;
      out.push({
        id: `${slug(categoria)}-unico`,
        categoria,
        variante,
        display,
        raw: `${categoria} - ${variante}`,
      });
    }
  });
  return out;
})();

/* =========================
   MODAL EDITOR DE BREAKS
   ========================= */


// ========================= PÁGINA =========================
export default function AdminAgendaPage({ readOnly = false, profesionalId = null }) {
  const [monthDate, setMonthDate] = useState(() => {
    const n = new Date(); n.setDate(1); n.setHours(0, 0, 0, 0); return n;
  });
  const [selectedDate, setSelectedDate] = useState(() => fmtYMD(new Date()));

  const [statsByDay, setStatsByDay] = useState({});
  const [dayBreaks, setDayBreaks] = useState([]);
  const [weeklyTemplate, setWeeklyTemplate] = useState({});
  const [effectiveBreaks, setEffectiveBreaks] = useState([]);
  const [dayReservations, setDayReservations] = useState([]);
  const [modalSesionesOpen, setModalSesionesOpen] = useState(false);
  const [modalPendientesOpen, setModalPendientesOpen] = useState(false);
  const [modalSolicitudesOpen, setModalSolicitudesOpen] = useState(false);
  const [modalTransferenciasOpen, setModalTransferenciasOpen] = useState(false);
  const [transferenciasList, setTransferenciasList] = useState([]);
  const [pendingList, setPendingList] = useState([]);

  const [kpi, setKpi] = useState({
    sesionesHoy: null,
    reservasPendientes: null,
    pagosPendientes: null,
    ocupacionSemana: null,
    ingresosMes: null, // NUEVO KPI
  });

  const [editorOpen, setEditorOpen] = useState(false);

  // Servicios para SlotModal
  const [servicios, setServicios] = useState([]);
  useEffect(() => { setServicios(ekineServicios); }, []);

  const [slotModal, setSlotModal] = useState({ open: false, slot: null });
  const openSlotModal = (slot) => setSlotModal({ open: true, slot });
  const closeSlotModal = () => setSlotModal({ open: false, slot: null });

  // 🔁 Invalidación de calendario
  const [refreshCount, setRefreshCount] = useState(0);
  const triggerRefresh = useCallback(() => setRefreshCount(c => c + 1), []);

  // Tras reprogramar, llevar la agenda al nuevo día (sin crear otra reserva).
  const handleReprogramada = useCallback(({ fechaNueva }) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(fechaNueva || "")) return;
    const [y, m] = fechaNueva.split("-").map(Number);
    setMonthDate(new Date(y, m - 1, 1));
    setSelectedDate(fechaNueva);
    triggerRefresh();
  }, [triggerRefresh]);

    // ---- Export CSV states ----
  const [csvFrom, setCsvFrom] = useState(() => fmtYMD(new Date(new Date().getFullYear(), new Date().getMonth(), 1)));
  const [csvTo,   setCsvTo]   = useState(() => fmtYMD(new Date()));

  
  const [estadoFilter, setEstadoFilter] = useState("activas");

  const estadosForUI = (f) => {
    if (f === "canceladas") return ["cancelada", "rechazada"];
    if (f === "todas") return ["pendiente", "aprobada", "realizada", "cancelada", "rechazada", "tentativa_break"];
    return ["pendiente", "aprobada", "realizada", "tentativa_break"];
  };

  const estadosForCSV = (f) => {
    if (f === "canceladas") return ["cancelada", "rechazada"];
    if (f === "todas") return ["pendiente", "aprobada", "realizada", "cancelada", "rechazada"];
    return ["pendiente", "aprobada", "realizada"];
  };

const refreshDay = useCallback(async () => {
  if (!selectedDate) return;

  const estados = estadosForUI(estadoFilter);

  const baseWheres = [
    where("fecha", "==", selectedDate),
    where("estado", "in", estados),
  ];

  if (profesionalId) {
    baseWheres.push(where("profesionalId", "==", profesionalId));
  }

  const qRes = query(collection(db, "reservas"), ...baseWheres);

  const snap = await getDocs(qRes);

  const arr = snap.docs.map((d) => {
    const data = d.data();
    if (data.esTentativa) data.estadoVisual = "tentativa_break";
    return { id: d.id, ...data };
  });

  setDayReservations(arr);
  triggerRefresh();
}, [selectedDate, estadoFilter, profesionalId, triggerRefresh]);


  // ---- KPI listeners ----
// ---- KPI listeners (FILTRADO POR PROFESIONAL) ----
useEffect(() => {
  const todayStr = fmtYMD(new Date());

  // ✅ Pendientes (solo del profesional si aplica)
  const whPend = [where("estado", "==", "pendiente")];
  if (profesionalId) whPend.push(where("profesionalId", "==", profesionalId));
  const qPend = query(collection(db, "reservas"), ...whPend);

  const unsubPend = onSnapshot(qPend, (snap) => {
    setKpi((k) => ({ ...k, reservasPendientes: snap.size || 0 }));
  });

  // ✅ Sesiones hoy (solo del profesional si aplica)
  const whHoy = [
    where("fecha", "==", todayStr),
    where("estado", "in", ["pendiente", "aprobada"]),
  ];
  if (profesionalId) whHoy.push(where("profesionalId", "==", profesionalId));
  const qHoy = query(collection(db, "reservas"), ...whHoy);

  const unsubHoy = onSnapshot(qHoy, (snap) => {
    setKpi((k) => ({ ...k, sesionesHoy: snap.size || 0 }));
  });

  // ✅ Pagos pendientes: OJO, tu colección "pagos" probablemente no tiene profesionalId.
  // Si SÍ lo tiene, filtra igual. Si NO lo tiene, este KPI será global sí o sí.
  const whPagos = [where("estado", "==", "pendiente")];
  if (profesionalId) whPagos.push(where("profesionalId", "==", profesionalId));
  const qPagosPend = query(collection(db, "pagos"), ...whPagos);

  const unsubPagos = onSnapshot(qPagosPend, (snap) => {
    setKpi((k) => ({ ...k, pagosPendientes: snap.size || 0 }));
  });

  return () => {
    unsubPend();
    unsubHoy();
    unsubPagos();
  };
}, [profesionalId]);


  // ---- LOAD: plantilla semanal de breaks ----
  useEffect(() => {
    const ref = doc(db, 'breaks_weekly', 'default');
    const unsub = onSnapshot(ref, (snap) => {
      const data = snap.data();
      setWeeklyTemplate(data?.defaultWeekly || {});
    });
    return () => unsub();
  }, []);

  // ---- STREAM: calendario del mes ----
  useEffect(() => {
    const s = startOfMonth(monthDate);
    const e = endOfMonth(monthDate);
    const sStr = fmtYMD(s), eStr = fmtYMD(e);

    const estados = estadosForUI(estadoFilter);

    const baseWheres = [
      where("fecha", ">=", sStr),
      where("fecha", "<=", eStr),
      where("estado", "in", estados),
    ];

    // ✅ si es worker, filtrar por su profesionalId
    if (profesionalId) {
      baseWheres.push(where("profesionalId", "==", profesionalId));
    }

    const qRes = query(collection(db, "reservas"), ...baseWheres);


    const unsub = onSnapshot(qRes, (snap) => {
      const acc = {};
        snap.forEach((d) => {
          const x = d.data();
          const f = x.fecha;
          if (!f) return;

          acc[f] =
            acc[f] || { aprobadas: 0, pendientes: 0, canceladas: 0, rechazadas: 0 };

          // 👇 contadores por estado
          if (x.estado === "aprobada" || x.estado === "realizada") acc[f].aprobadas += 1;
          else if (x.estado === "pendiente" || x.estado === "tentativa_break") acc[f].pendientes += 1;
          else if (x.estado === "cancelada") acc[f].canceladas += 1;
          else if (x.estado === "rechazada") acc[f].rechazadas += 1;
        });
      setStatsByDay(acc);
    });

    return () => unsub();
  }, [monthDate, refreshCount, estadoFilter, profesionalId]);

  // ---- STREAM: día seleccionado ----
  useEffect(() => {
    if (!selectedDate) return;

    const estados = estadosForUI(estadoFilter);

    const baseWheres = [
      where("fecha", "==", selectedDate),
      where("estado", "in", estados),
    ];

    if (profesionalId) {
      baseWheres.push(where("profesionalId", "==", profesionalId));
    }

    const qRes = query(collection(db, "reservas"), ...baseWheres);

    const unsubRes = onSnapshot(qRes, (snap) => {
      const arr = snap.docs.map((d) => {
  const data = d.data();
  if (data.esTentativa) data.estadoVisual = 'tentativa_break';
  return { id: d.id, ...data };
});

      setDayReservations(arr);
    });

    const qBreaks = query(collection(db, 'breaks'), where('fecha', '==', selectedDate));
    const unsubBr = onSnapshot(qBreaks, (snap) => {
      const arr = [];
      snap.forEach((d) => {
        const b = d.data();
        if (b?.start && b?.end) arr.push({ start: b.start, end: b.end, flexible: !!b.flexible });
      });
      setDayBreaks(arr);
    });

    return () => { unsubRes(); unsubBr(); };
    }, [selectedDate, estadoFilter, profesionalId]);

  // ---- MERGE: override del día > plantilla semanal ----
  useEffect(() => {
    const w = String(weekdayISO(selectedDate));
    const weekly = weeklyTemplate?.[w] || [];
    setEffectiveBreaks((dayBreaks?.length || 0) > 0 ? dayBreaks : weekly);
  }, [selectedDate, dayBreaks, weeklyTemplate]);

  // ---- STREAM: pendientes/tentativas (FILTRADO POR PROFESIONAL) ----
  useEffect(() => {
    const wh = [
      where("estado", "in", ["pendiente", "tentativa_break"]),
      orderBy("creadoEn", "asc"),
      limit(200),
    ];

    if (profesionalId) {
      wh.unshift(where("profesionalId", "==", profesionalId));
    }

    const qPend = query(collection(db, "reservas"), ...wh);

    const unsub = onSnapshot(qPend, (snap) => {
      const arr = snap.docs.map((d) => {
        const data = d.data();
        if (data.esTentativa) data.estadoVisual = "tentativa_break";
        return { id: d.id, ...data };
      });

      setPendingList(arr);
    });

    return () => unsub();
  }, [profesionalId]);


  // ---- KPI: ocupación semanal ----
  // Ajusta a tu operación real:
  const CAPACIDAD_SEMANA = 1 * 12 * 6; // N_PROF * SLOTS_POR_DIA * DIAS_HABILES
  const semanaRango = () => {
    const d = new Date();
    const day = d.getDay() || 7; // 1..7 (L..D)
    const monday = new Date(d); monday.setDate(d.getDate() - (day-1));
    const sunday = new Date(monday); sunday.setDate(monday.getDate()+6);
    const f = x => fmtYMD(x);
    return { ini: f(monday), fin: f(sunday) };
  };

useEffect(() => {
  const { ini, fin } = semanaRango();

  const whSem = [
    where("fecha", ">=", ini),
    where("fecha", "<=", fin),
    where("estado", "in", ["pendiente", "aprobada", "realizada"]),
  ];
  if (profesionalId) whSem.push(where("profesionalId", "==", profesionalId));

  const qSem = query(collection(db, "reservas"), ...whSem);

  const unsub = onSnapshot(qSem, (snap) => {
    const tomadas = snap.size || 0;
    const occ = CAPACIDAD_SEMANA ? Math.round((tomadas / CAPACIDAD_SEMANA) * 100) : 0;
    setKpi((k) => ({ ...k, ocupacionSemana: occ }));
  });

  return () => unsub();
}, [CAPACIDAD_SEMANA, profesionalId]);


// ---- KPI: transferencias recibidas del mes (MES ACTUAL) + detalle
useEffect(() => {
  const now = new Date();

  const iniDate = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
  const nextMonthDate = new Date(now.getFullYear(), now.getMonth() + 1, 1, 0, 0, 0, 0);

  const iniTs = Timestamp.fromDate(iniDate);
  const finTs = Timestamp.fromDate(nextMonthDate); // exclusivo

  const wh = [
    where("creadoEn", ">=", iniTs),
    where("creadoEn", "<", finTs),
    where("estado", "in", ["pendiente", "aprobada", "realizada"]),
    orderBy("creadoEn", "desc"),
    limit(300),
  ];

  if (profesionalId) wh.unshift(where("profesionalId", "==", profesionalId));

  const qMes = query(collection(db, "reservas"), ...wh);

  const unsub = onSnapshot(
    qMes,
    (snap) => {
      let total = 0;
      const rows = [];

      snap.forEach((d) => {
        const x = d.data();
        const mp = x?.monto_pagado;
        const metodo = (x?.pago_metodo ?? "").toString().trim(); // puede venir vacío/undefined
        const cuentaComoTransfer = metodo === "transferencia" || metodo === "" || metodo === null;

        if (cuentaComoTransfer && typeof mp === "number" && mp > 0) {
          total += mp;
          rows.push({ id: d.id, ...x });
        }
      });

      setKpi((k) => ({ ...k, ingresosMes: total }));
      setTransferenciasList(rows);
    },
    (err) => console.error("KPI transferencias:", err)
  );

  return () => unsub();
}, [profesionalId]);


// === Solicitudes de cambio / cancelación (FILTRADO POR PROFESIONAL) ===
const [solicitudes, setSolicitudes] = useState([]);

useEffect(() => {
  const wh = [];
  if (profesionalId) wh.push(where("profesionalId", "==", profesionalId));

  const qSol = wh.length
    ? query(collection(db, "solicitudes_modificacion"), ...wh)
    : collection(db, "solicitudes_modificacion");

  const unsub = onSnapshot(qSol, (snap) => {
    const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    const pendientes = data.filter((s) => s.estado === "pendiente");
    setSolicitudes(pendientes);
  });

  return () => unsub();
}, [profesionalId]);


const aprobarSolicitud = async (sol) => {
  try {
    await updateDoc(doc(db, "solicitudes_modificacion", sol.id), {
      estado: "aprobada",
    });
    if (sol.tipo === "cancelacion" && sol.reservaId) {
      await updateDoc(doc(db, "reservas", sol.reservaId), {
        estado: "cancelada",
      });
    }
    alert("✅ Solicitud aprobada correctamente.");
  } catch (err) {
    console.error(err);
    alert("❌ Error al aprobar la solicitud.");
  }
};

const rechazarSolicitud = async (sol) => {
  try {
    await updateDoc(doc(db, "solicitudes_modificacion", sol.id), {
      estado: "rechazada",
    });
    alert("🚫 Solicitud rechazada.");
  } catch (err) {
    console.error(err);
    alert("❌ Error al rechazar la solicitud.");
  }
};



  // --------- Helpers ----------
  // eslint-disable-next-line no-unused-vars
  const slotOcupado = async (fecha, hora, excludeId) => {
    const q = query(
      collection(db, 'reservas'),
      where('fecha', '==', fecha),
      where('hora', '==', hora),
      where('estado', 'in', ['aprobada', 'pendiente', 'tentativa_break'])
    );
    const snap = await getDocs(q);
    const others = snap.docs.filter((d) => d.id !== excludeId);
    return others.length > 0;
  };

  // --------- Acciones reservas ----------
  const aprobarNormal = async (reserva) => {
  try {
    let fecha = reserva.fecha;
    let hora = reserva.hora;

    // 🟨 Si es tentativa, usa el horario principal
    if (reserva.esTentativa && reserva.primary) {
      fecha = reserva.primary.fecha;
      hora = reserva.primary.hora;
    }

    if (!fecha || !hora)
      return alert("Datos insuficientes para aprobar la reserva.");

    await aprobarReservaSegura(db, {
      reservaId: reserva.id,
      fecha,
      hora,
      durationMin: reserva.durationMin || 60,
      bufferMin: reserva.bufferMin || 10,
      pagoId: reserva.pagoId || null,
    });

    await refreshDay();
  } catch (e) {
    alert(e.message || "No se pudo aprobar la reserva.");
  }
};


  const aprobarTentativaElegida = async (reserva, cual /* 'primary'|'fallback' */) => {
  const primary = reserva.primary || { fecha: reserva.fecha, hora: reserva.hora };
  const fb = reserva.fallback || null;
  const chosen = cual === "fallback" && fb ? fb : primary;

  if (!chosen?.fecha || !chosen?.hora)
    return alert("No hay horario válido para aprobar.");

  try {
    await updateDoc(doc(db, "reservas", reserva.id), {
      fecha: chosen.fecha,
      hora: chosen.hora,
      chosenSlot: cual,
      updatedAt: serverTimestamp(),
    });

    await aprobarReservaSegura(db, {
      reservaId: reserva.id,
      fecha: chosen.fecha,
      hora: chosen.hora,
      durationMin: reserva.durationMin || 60,
      bufferMin: reserva.bufferMin || 10,
      pagoId: reserva.pagoId || null,
    });

    await refreshDay();
  } catch (e) {
    alert(e.message || "Error al aprobar la tentativa.");
  }
};


  const rechazarConMotivo = async (reserva, motivo) => {
    await updateDoc(doc(db, 'reservas', reserva.id), {
      estado: 'rechazada',
      rechazoMotivo: motivo,
      updatedAt: serverTimestamp(),
    });
    await refreshDay();
  };

  // --------- Acciones breaks ----------
  const saveWeekly = async () => {
    const ref = doc(db, 'breaks_weekly', 'default');
    await setDoc(ref, { defaultWeekly: weeklyTemplate || {}, updatedAt: serverTimestamp() }, { merge: true });
    alert('Plantilla semanal guardada.');
    setEditorOpen(false);
    triggerRefresh();
  };

  const saveDaily = async () => {
    const qOld = query(collection(db, 'breaks'), where('fecha', '==', selectedDate));
    const old = await getDocs(qOld);
    const batch = writeBatch(db);
    old.forEach((d) => batch.delete(d.ref));
    await batch.commit();

    for (const b of dayBreaks || []) {
      if (b.start && b.end && b.start < b.end) {
        await addDoc(collection(db, 'breaks'), {
          fecha: selectedDate,
          start: b.start,
          end: b.end,
          flexible: !!b.flexible,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
    }
    alert(`Breaks guardados para ${selectedDate}.`);
    setEditorOpen(false);
    triggerRefresh();
  };

  // ---- Export CSV ----
  const exportCSV = async () => {
    try {
      if (!csvFrom || !csvTo || csvFrom > csvTo) {
        alert('Rango de fechas inválido');
        return;
      }
      const qRes = query(
        collection(db, 'reservas'),
        where('fecha','>=', csvFrom),
        where('fecha','<=', csvTo),
        orderBy('fecha', 'asc')
      );
      const snap = await getDocs(qRes);
      const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      const headers = [
        'id','fecha','hora','estado','estadoSesion','nombre','email',
        'servicio_categoria','servicio_variante','monto',
        'profesionalNombre','profesionalId','origen','creadoEn','updatedAt'
      ];

      const valueOf = (r, key) => {
        if (key === 'creadoEn' || key === 'updatedAt') {
          const ts = r[key];
          if (ts && ts.toDate) return ts.toDate().toISOString();
          return r[key] || '';
        }
        return r?.[key] ?? '';
      };

      const line = (r) => headers.map(h => JSON.stringify(valueOf(r, h))).join(',');
      const csv = [headers.join(','), ...rows.map(line)].join('\n');

      const blob = new Blob([csv], { type:'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `reservas_${csvFrom}_a_${csvTo}.csv`;
      a.click(); URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert('No se pudo exportar.');
    }
  };

  // ========================= RENDER =========================
  return (
    <section className="p-4 sm:p-6">
      <header className="mb-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Agenda</h1>
          <p className="text-sm text-slate-600">Calendario mensual, agenda del día y pendientes por aprobar.</p>
        </div>

        {/* === Rango de fechas === */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full sm:w-auto">

          {/* === BLOQUE CSV (OCULTO EN MÓVIL) === */}
          <div className="hidden md:flex items-center gap-2 bg-white border rounded-xl px-3 py-2">
            {/* === FILTRO ESTADOS === */}
            <div className="flex items-center bg-slate-100 rounded-xl p-1 mr-2">
              <button
                onClick={() => setEstadoFilter("activas")}
                className={`px-3 py-1.5 rounded-lg text-sm transition ${
                  estadoFilter === "activas"
                    ? "bg-white shadow text-slate-900"
                    : "text-slate-600 hover:text-slate-900"
                }`}
                title="Pendientes / Aprobadas / Realizadas"
              >
                Activas
              </button>

              <button
                onClick={() => setEstadoFilter("canceladas")}
                className={`px-3 py-1.5 rounded-lg text-sm transition ${
                  estadoFilter === "canceladas"
                    ? "bg-white shadow text-slate-900"
                    : "text-slate-600 hover:text-slate-900"
                }`}
                title="Canceladas / Rechazadas"
              >
                Canceladas
              </button>

              <button
                onClick={() => setEstadoFilter("todas")}
                className={`px-3 py-1.5 rounded-lg text-sm transition ${
                  estadoFilter === "todas"
                    ? "bg-white shadow text-slate-900"
                    : "text-slate-600 hover:text-slate-900"
                }`}
                title="Todas"
              >
                Todas
              </button>
            </div>

            <input
              type="date"
              value={csvFrom}
              max={csvTo}
              onChange={(e) => setCsvFrom(e.target.value)}
              className="text-sm sm:text-base border border-gray-300 rounded-lg px-3 py-2 bg-white text-slate-900 focus:outline-none appearance-none"
            />

            <span className="text-slate-400">→</span>

            <input
              type="date"
              value={csvTo}
              min={csvFrom}
              onChange={(e) => setCsvTo(e.target.value)}
              className="text-sm sm:text-base border border-gray-300 rounded-lg px-3 py-2 bg-white text-slate-900 focus:outline-none appearance-none"
            />

            <button
              onClick={exportCSV}
              className="ml-2 px-3 py-1.5 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-800"
            >
              Exportar CSV
            </button>
          </div>

          {/* === ESTE SÍ SE VE EN MÓVIL === */}
          {!readOnly && (
            <button
              onClick={() => setEditorOpen(true)}
              className="px-3 py-2 rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 w-full sm:w-auto"
            >
              Editar breaks
            </button>
          )}
        </div>
      </header>

      
     <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
  <KpiCard
    icon={<CalendarClock className="h-5 w-5" />}
    title="Sesiones hoy"
    value={kpi.sesionesHoy}
    accent="bg-emerald-500/10 text-emerald-700"
    onClick={() => setModalSesionesOpen(true)}
  />
  <KpiCard
    icon={<Clock4 className="h-5 w-5" />}
    title="Reservas pendientes"
    value={kpi.reservasPendientes}
    accent="bg-amber-500/10 text-amber-700"
    onClick={() => setModalPendientesOpen(true)}
  />
  <KpiCard
    icon={<AlertTriangle className="h-5 w-5" />}
    title="Reservas con solicitud de cambio/cancelación"
    value={solicitudes.length}
    accent="bg-orange-500/10 text-orange-700"
    onClick={() => setModalSolicitudesOpen(true)}
  />

  {!readOnly && (
    <KpiCard
      icon={<DollarSign className="h-5 w-5" />}
      title="Transferencias del mes"
      value={kpi.ingresosMes == null ? null : `$${kpi.ingresosMes.toLocaleString("es-CL")}`}
      accent="bg-green-500/10 text-green-700"
      onClick={() => setModalTransferenciasOpen(true)}
    />)}
    </section>
    

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <AgendaCalendar
            monthDate={monthDate}
            onMonthChange={setMonthDate}
            selectedDate={selectedDate}
            onSelectDate={setSelectedDate}
            statsByDay={statsByDay}
            refreshKey={refreshCount}
            estadoFilter={estadoFilter}
          />
        </div>
        <div>
        <DayAgenda
          readOnly={readOnly}
          date={selectedDate}
          reservations={dayReservations || []}
          breaks={effectiveBreaks || []}
          onSlotClick={(slot) => {
            setSlotModal({
              open: true,
              slot: {
                ...slot,
                dateISO: selectedDate
              }
            });
          }}
        />
        </div>
      </div>

      {!readOnly && <BreakEditorModal
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        selectedDate={selectedDate}
        weeklyTemplate={weeklyTemplate}
        setWeeklyTemplate={setWeeklyTemplate}
        dayBreaks={dayBreaks}
        setDayBreaks={setDayBreaks}
        onSaveWeekly={saveWeekly}
        onSaveDaily={saveDaily}
      />}

      <SlotModal
        readOnly={readOnly}
        isOpen={slotModal.open}
        slot={slotModal.slot}
        onClose={closeSlotModal}
        servicios={servicios}
        onRefresh={refreshDay} // 👈 sincroniza vista diaria y calendario
        onReprogramada={handleReprogramada}
      />
{/* ===================== MODALES DE KPI ===================== */}

{/* Modal: Transferencias del mes */}
{modalTransferenciasOpen && (
  <div
    className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
    onClick={() => setModalTransferenciasOpen(false)}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      className="bg-white/90 backdrop-blur-2xl border border-white/40 rounded-2xl shadow-2xl w-full max-w-4xl mx-4 p-6 relative"
    >
      <h2 className="text-2xl font-bold text-blue-900 mb-1">
        Transferencias del mes
      </h2>

      <p className="text-sm text-slate-600 mb-4">
        Total: <strong>${(kpi.ingresosMes || 0).toLocaleString("es-CL")}</strong> ·
        {" "}Registros: <strong>{transferenciasList.length}</strong>
      </p>

      {transferenciasList.length === 0 ? (
        <p className="text-gray-600 text-center py-10">
          No hay transferencias registradas este mes (según filtros actuales).
        </p>
      ) : (
        <ul className="space-y-3 max-h-[65vh] overflow-y-auto pr-2">
          {transferenciasList.map((r) => {
            const creado =
              r?.creadoEn?.toDate ? r.creadoEn.toDate().toLocaleString("es-CL") : "—";
            const metodo = r?.pago_metodo ? r.pago_metodo : "—";

            return (
              <li
                key={r.id}
                onClick={() => {
                  setModalTransferenciasOpen(false);
                  setSlotModal({
                    open: true,
                    slot: { reservaId: r.id, status: "reserved" },
                  });
                }}
                className="p-3 bg-white border rounded-xl shadow-sm hover:shadow-md hover:bg-green-50 transition cursor-pointer"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-900 truncate">
                      {r.nombre || "—"}
                    </p>
                    <p className="text-sm text-slate-600 truncate">
                      {r.servicio_categoria || "—"} — {r.servicio_variante || "—"}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      Creado: {creado} · Estado: {r.estado || "—"} · Método: {metodo}
                    </p>
                  </div>

                  <div className="shrink-0 text-right">
                    <p className="font-bold text-slate-900 tabular-nums">
                      ${Number(r.monto_pagado || 0).toLocaleString("es-CL")}
                    </p>
                    <p className="text-xs text-slate-500">
                      Pago tipo: {r.pago_tipo || r.pagoEstado || "—"}
                    </p>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <button
        onClick={() => setModalTransferenciasOpen(false)}
        className="absolute top-4 right-4 text-gray-600 hover:text-gray-800 text-lg"
      >
        ✕
      </button>
    </div>
  </div>
)}

{/* Modal: Sesiones hoy */}
{modalSesionesOpen && (
  <div
    className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
    onClick={() => setModalSesionesOpen(false)}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      className="bg-white/90 backdrop-blur-2xl border border-white/40 rounded-2xl shadow-2xl w-full max-w-3xl mx-4 p-6 relative"
    >
      <h2 className="text-2xl font-bold text-blue-900 mb-4">
        Sesiones del día ({kpi.sesionesHoy})
      </h2>

      {dayReservations.length === 0 ? (
        <p className="text-gray-600 text-center py-8">
          No hay sesiones registradas hoy.
        </p>
      ) : (
        <ul className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
          {dayReservations.map((r) => (
            <li
              key={r.id}
              className="p-3 bg-white border rounded-xl shadow-sm hover:shadow-md transition"
            >
              <p className="font-semibold text-slate-900">{r.nombre}</p>
              <p className="text-sm text-slate-600">
                {r.servicio_categoria} — {r.hora}
              </p>
            </li>
          ))}
        </ul>
      )}

      <button
        onClick={() => setModalSesionesOpen(false)}
        className="absolute top-4 right-4 text-gray-600 hover:text-gray-800 text-lg"
      >
        ✕
      </button>
    </div>
  </div>
)}

{/* Modal: Reservas pendientes */}
{/* Modal: Reservas pendientes (solo lista clickeable) */}
{modalPendientesOpen && (
  <div
    className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
    onClick={() => setModalPendientesOpen(false)}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      className="bg-white/90 backdrop-blur-2xl border border-white/40 rounded-2xl shadow-2xl w-full max-w-3xl mx-4 p-6 relative"
    >
      <h2 className="text-2xl font-bold text-blue-900 mb-4">
        Reservas pendientes
      </h2>

      {pendingList.length === 0 ? (
        <p className="text-gray-600 text-center py-8">
          No hay reservas pendientes por aprobar.
        </p>
      ) : (
        <ul className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
          {pendingList.map((r) => (
            <li
              key={r.id}
              onClick={() =>
  setSlotModal({
    open: true,
    slot: {
      ...r,
      reservaId: r.id,   // 👉 para que SlotModal cargue el detalle real
      status: "reserved" // 👉 fuerza modo "detail" en lugar de "new"
    }
  })
}

              className="p-3 bg-white border rounded-xl shadow-sm hover:shadow-md hover:bg-blue-50 transition cursor-pointer"
            >
              <p className="font-semibold text-slate-900">{r.nombre}</p>
              <p className="text-sm text-slate-600">
                {r.servicio_categoria} — {r.fecha} {r.hora}
              </p>
            </li>
          ))}
        </ul>
      )}

      <button
        onClick={() => setModalPendientesOpen(false)}
        className="absolute top-4 right-4 text-gray-600 hover:text-gray-800 text-lg"
      >
        ✕
      </button>
    </div>
  </div>
)}

{/* ===================== MODALES DE KPI ===================== */}

{/* Modal: Sesiones hoy */}
{modalSesionesOpen && (
  <div
    className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
    onClick={() => setModalSesionesOpen(false)}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      className="bg-white/90 backdrop-blur-2xl border border-white/40 rounded-2xl shadow-2xl w-full max-w-3xl mx-4 p-6 relative"
    >
      <h2 className="text-2xl font-bold text-blue-900 mb-4">
        Sesiones del día ({kpi.sesionesHoy})
      </h2>

      {dayReservations.length === 0 ? (
        <p className="text-gray-600 text-center py-8">
          No hay sesiones registradas hoy.
        </p>
      ) : (
        <ul className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
          {dayReservations.map((r) => (
            <li
              key={r.id}
              onClick={() => {
                setModalSesionesOpen(false);
                setSlotModal({
                  open: true,
                  slot: {
                    ...r,
                    reservaId: r.id,
                    status: "reserved", // fuerza modo detalle
                  },
                });
              }}
              className="p-3 bg-white border rounded-xl shadow-sm hover:shadow-md hover:bg-blue-50 transition cursor-pointer"
            >
              <p className="font-semibold text-slate-900">{r.nombre}</p>
              <p className="text-sm text-slate-600">
                {r.servicio_categoria} — {r.fecha} {r.hora}
              </p>
            </li>
          ))}
        </ul>
      )}

      <button
        onClick={() => setModalSesionesOpen(false)}
        className="absolute top-4 right-4 text-gray-600 hover:text-gray-800 text-lg"
      >
        ✕
      </button>
    </div>
  </div>
)}

{/* Modal: Reservas pendientes */}
{modalPendientesOpen && (
  <div
    className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
    onClick={() => setModalPendientesOpen(false)}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      className="bg-white/90 backdrop-blur-2xl border border-white/40 rounded-2xl shadow-2xl w-full max-w-3xl mx-4 p-6 relative"
    >
      <h2 className="text-2xl font-bold text-blue-900 mb-4">
        Reservas pendientes
      </h2>

      {pendingList.length === 0 ? (
        <p className="text-gray-600 text-center py-8">
          No hay reservas pendientes por aprobar.
        </p>
      ) : (
        <ul className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
          {pendingList.map((r) => (
            <li
              key={r.id}
              onClick={() => {
                setModalPendientesOpen(false);
                setSlotModal({
                  open: true,
                  slot: {
                    ...r,
                    reservaId: r.id,
                    status: "reserved",
                  },
                });
              }}
              className="p-3 bg-white border rounded-xl shadow-sm hover:shadow-md hover:bg-blue-50 transition cursor-pointer"
            >
              <p className="font-semibold text-slate-900">{r.nombre}</p>
              <p className="text-sm text-slate-600">
                {r.servicio_categoria} — {r.fecha} {r.hora}
              </p>
            </li>
          ))}
        </ul>
      )}

      <button
        onClick={() => setModalPendientesOpen(false)}
        className="absolute top-4 right-4 text-gray-600 hover:text-gray-800 text-lg"
      >
        ✕
      </button>
    </div>
  </div>
)}

{/* Modal: Solicitudes de cambio o cancelación */}
{modalSolicitudesOpen && (
  <div
    className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
    onClick={() => setModalSolicitudesOpen(false)}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      className="bg-white/90 backdrop-blur-2xl border border-white/40 rounded-2xl shadow-2xl w-full max-w-3xl mx-4 p-6 relative"
    >
      <h2 className="text-2xl font-bold text-blue-900 mb-4">
        Solicitudes de cambio o cancelación
      </h2>

      {solicitudes.length === 0 ? (
        <p className="text-gray-600 text-center py-8">
          No hay solicitudes pendientes.
        </p>
      ) : (
        <ul className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
          {solicitudes.map((s) => (
            <li
              key={s.id}
              onClick={() => {
                if (!s.reservaId) return alert("Esta solicitud no tiene una reserva asociada.");
                setModalSolicitudesOpen(false);
                setSlotModal({
                  open: true,
                  slot: {
                    reservaId: s.reservaId,
                    status: "reserved",
                  },
                });
              }}
              className="p-3 bg-white border rounded-xl shadow-sm hover:shadow-md hover:bg-orange-50 transition cursor-pointer"
            >
              <p className="font-semibold text-blue-900">
                {s.tipo === "cancelacion"
                  ? "🟥 Cancelación y reembolso"
                  : "🟨 Cambio de horario"}
              </p>
              <p className="text-sm text-gray-700 mt-1">
                <strong>Usuario:</strong> {s.nombre_usuario || "—"}
              </p>
              <p className="text-sm text-gray-700">
                <strong>Reserva ID:</strong> {s.reservaId}
              </p>
            </li>
          ))}
        </ul>
      )}

      <button
        onClick={() => setModalSolicitudesOpen(false)}
        className="absolute top-4 right-4 text-gray-600 hover:text-gray-800 text-lg"
      >
        ✕
      </button>
    </div>
  </div>
)}


    </section>
  );
}
